import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'

import {
  useMarkRead,
  useMembers,
  useMessages,
  useOlderMessages,
  useTrip,
} from '../../hooks/queries'
import type { Member } from '../../api/types'
import { dayLabel, sameDay } from '../../lib/format'
import { BackIcon, InfoIcon } from '../../components/Icons'
import { useOutbox } from '../../hooks/useOutbox'
import { Bubble } from './Bubble'
import { Composer } from './Composer'
import { Wartend } from './Wartend'

/**
 * Ein Chat.
 *
 * Ohne `userId` ist das die Reise selbst: für einen Empfänger der Einzelchat
 * mit dem Reisenden, für den Absender sein Rundbrief. Mit `userId` der
 * Antwortverlauf eines Empfängers -- den kann nur der Absender öffnen, und
 * das Backend prüft das noch einmal selbst.
 */
export function ThreadPage() {
  const params = useParams()
  const tripId = Number(params.tripId)
  const withUser = params.userId ? Number(params.userId) : undefined

  const trip = useTrip(tripId)
  const messages = useMessages(tripId, withUser)
  const older = useOlderMessages(tripId, withUser)
  const markRead = useMarkRead(tripId, withUser)
  // Der Name des Empfängers kommt aus der Teilnehmerliste, nicht aus den
  // Nachrichten: wer noch nie geantwortet hat, käme darin gar nicht vor.
  const members = useMembers(tripId, withUser !== undefined)
  // Was in diesem Verlauf noch auf Netz wartet -- steht unten, vor der
  // Eingabezeile, als wäre es schon gesendet. Denn das ist es aus Sicht
  // dessen, der es geschrieben hat.
  const wartende = useOutbox(tripId, withUser)

  const newest = messages.data?.messages[0]?.id
  useEffect(() => {
    // Wer den Chat offen hat, hat gelesen. Ausgelöst wird das an der neuesten
    // Nachricht, nicht bei jedem Rendern -- sonst schriebe jedes Scrollen.
    if (newest !== undefined) markRead.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, withUser, newest])

  // Ein fehlgeschlagener Abruf darf nicht wie ein leerer Chat aussehen --
  // sonst wirkt eine verweigerte Berechtigung wie „hier steht eben nichts“.
  const failure = trip.error ?? messages.error
  if (failure) {
    return (
      <div className="empty">
        <p className="empty__title">Dieser Chat lässt sich nicht öffnen</p>
        <p>{failure.message}</p>
        <Link className="button button--quiet" to="/">
          Zurück zur Liste
        </Link>
      </div>
    )
  }

  const partner = members.data?.find((m) => m.user.id === withUser)
  const partnerName = partner?.user.display_name

  // Warum hier nichts zu schreiben ist -- oder null, wenn es geht.
  //
  // Beurteilt wird erst, wenn die Teilnehmerliste **da** ist: solange sie
  // lädt, ist „nicht gefunden" kein Befund, sondern ein Zwischenstand, und
  // „nicht mehr in dieser Reise" blitzte bei jedem Öffnen kurz auf.
  const stumm = withUser === undefined || !members.isSuccess ? null : grundOhneZeile(partner)

  // Und solange es noch offen ist, steht unten gar nichts. Eine Eingabezeile,
  // die eine Zehntelsekunde später verschwindet, ist dieselbe Verwirrung in
  // klein -- lieber einen Augenblick nichts als kurz ein falsches Versprechen.
  const wartetAufAuskunft = withUser !== undefined && members.isPending

  const title = withUser ? (partnerName ?? 'Antworten') : (trip.data?.title ?? '…')

  const zweck = withUser
    ? `Antworten in „${trip.data?.title ?? ''}“ – nur für dich sichtbar`
    : trip.data?.role === 'sender'
      ? `Rundbrief an ${(trip.data.member_count ?? 1) - 1} Empfänger` +
        // Wer wartet, wartet auf eine Entscheidung -- und die trifft niemand,
        // der nichts davon weiß. Die Reiseseite ist einen Tipp entfernt.
        (trip.data.pending_requests > 0
          ? ` · ${trip.data.pending_requests} ${trip.data.pending_requests === 1 ? 'Anfrage' : 'Anfragen'}`
          : '')
      : `Reisebericht von ${trip.data?.owner.display_name ?? ''}`

  // Die Beschreibung stand bisher nur auf der Infoseite -- also dort, wo man
  // sie beim Lesen nie sieht. Sie gehört in den Kopf des Chats, denn sie sagt,
  // worum es in dieser Reise geht.
  //
  // Eigene Zeile statt angehängt: hinter „Rundbrief an 5 Empfänger" wäre sie
  // auf einem Telefon nach drei Wörtern zu Ende. So bekommt sie zwei Zeilen,
  // und die Angaben darüber -- vor allem eine wartende Anfrage -- bleiben
  // unangetastet. Der Kopf wächst nur dort, wo es überhaupt etwas zu sagen
  // gibt.
  const beschreibung = withUser ? '' : (trip.data?.description ?? '').trim()

  const list = messages.data?.messages ?? []
  const olderCursor = messages.data?.next_before ?? null

  return (
    <>
      <header className="bar">
        <Link className="icon-button" to="/" aria-label="Zurück zur Liste">
          <BackIcon />
        </Link>
        <div className="bar__grow">
          <h2 className="bar__title">{title}</h2>
          <p className="bar__subtitle">{zweck}</p>
          {/* `title`: bei mehr als zwei Zeilen lässt sie sich wenigstens im
              Vorbeigehen lesen -- ganz steht sie auf der Infoseite. */}
          {beschreibung !== '' && (
            <p className="bar__desc" title={beschreibung}>
              {beschreibung}
            </p>
          )}
        </div>
        <Link
          className="icon-button"
          to={`/t/${tripId}/info`}
          aria-label="Angaben zur Reise"
        >
          <InfoIcon />
        </Link>
      </header>

      {/* column-reverse: die neueste Nachricht steht unten, und der Browser
          hält den Blick von selbst dort, wenn eine dazukommt. */}
      <div className="timeline">
        {messages.isPending && <p className="empty">Wird geladen …</p>}

        {!messages.isPending && list.length === 0 && (
          <div className="empty">
            <p className="empty__title">Noch nichts geschrieben</p>
            <p>
              {withUser
                ? `${partnerName ?? 'Diese Person'} hat noch nicht geantwortet.`
                : trip.data?.role === 'sender'
                  ? 'Schreib die erste Nachricht – alle Empfänger bekommen sie zu lesen.'
                  : 'Sobald der Reisende etwas schreibt, erscheint es hier.'}
            </p>
          </div>
        )}

        {/* Vor der Liste, weil die Zeitleiste umgekehrt läuft (column-reverse):
            was hier steht, erscheint unten -- und dort gehört das hin, was als
            Nächstes rausgeht. */}
        {[...wartende].reverse().map((eintrag) => (
          <Wartend key={`wartet-${eintrag.key}`} eintrag={eintrag} />
        ))}

        {list.map((message, index) => {
          const next = list[index + 1]
          const startsDay = !next || !sameDay(message.created_at, next.created_at)
          return (
            <div key={message.id} style={{ display: 'contents' }}>
              <Bubble
                message={message}
                tripId={tripId}
                withUser={withUser}
                showAuthor={!message.own && Boolean(withUser)}
                canReact={trip.data?.archived_at == null}
                // Wer das liest, entscheidet über den Wortlaut, und nur diese
                // Seite kennt beide Blickwinkel: der Mitleser soll nicht
                // glauben, eine persönliche Nachricht sei ein Rundbrief -- und
                // der Reisende sieht in ihrem Verlauf sonst nicht, was an alle
                // ging und was nur an sie.
                // Der Rückfall nennt keine Person, sondern „diese Person":
                // solange der Name noch nicht da ist, wäre jedes Fürwort
                // geraten -- und geraten heißt hier: bei jedem zweiten falsch.
                directLabel={withUser ? `Nur an ${partnerName ?? 'diese Person'}` : 'Nur an dich'}
              />
              {startsDay && (
                <div className="day-divider">{dayLabel(message.created_at)}</div>
              )}
            </div>
          )
        })}

        {olderCursor != null && (
          <button
            type="button"
            className="button button--outline"
            style={{ margin: '10px auto', width: 'auto' }}
            disabled={older.isPending}
            onClick={() => older.mutate(olderCursor)}
          >
            {older.isPending ? 'Wird geladen …' : 'Ältere Nachrichten'}
          </button>
        )}
      </div>

      {/* Hier stand lange der Satz „Schreiben kannst du im Reise-Chat an
          alle." -- der Verlauf war einseitig: sie konnte ihm schreiben, er ihr
          nicht. Jetzt geht auch die andere Richtung, und was hier hinausgeht,
          sieht ausschließlich diese eine Person. */}
      {withUser && !wartetAufAuskunft && stumm === null && trip.data?.archived_at == null && (
        <p className="composer__hint composer__hint--privat">
          Was du hier schreibst, sieht nur {partnerName ?? 'diese Person'}.
        </p>
      )}

      {!wartetAufAuskunft && <Composer
        tripId={tripId}
        withUser={withUser}
        disabled={trip.data?.archived_at != null || stumm !== null}
        // Die abgeschlossene Reise hat Vorrang: sie gilt für alle Verläufe,
        // und ein Grund, der nur diesen einen betrifft, wäre daneben falsch.
        disabledHint={
          trip.data?.archived_at != null ? undefined : (
            <>
              {stumm?.text.replace('{name}', partnerName ?? 'Diese Person')}
              {stumm?.zurReise && (
                <>
                  {' '}
                  <Link to={`/t/${tripId}/info`}>Auf der Reiseseite ändern</Link>
                </>
              )}
            </>
          )
        }
        placeholder={
          withUser
            ? `Nachricht nur an ${partnerName ?? 'diese Person'}`
            : trip.data?.role === 'sender'
              ? 'Nachricht an alle Empfänger'
              : `Antwort an ${trip.data?.owner.display_name ?? ''}`
        }
      />}
    </>
  )
}

/**
 * Warum an diesen Menschen gerade nichts geht — oder null, wenn es geht.
 *
 * Die Reihenfolge ist die der Endgültigkeit: wer gar nicht mehr dabei ist,
 * bleibt es; ein stillgelegtes Konto kann die Verwaltung wieder freigeben; ein
 * Stichtag ist ein Handgriff auf der Reiseseite. Genannt wird immer der
 * Zustand, der die Nachricht **zuerst** aufhält.
 *
 * `{name}` wird von der Seite ersetzt — hier steht der Name noch nicht fest,
 * weil er aus derselben Liste kommt, die auch diesen Zustand liefert.
 */
function grundOhneZeile(
  partner: Member | undefined,
): { text: string; zurReise: boolean } | null {
  if (partner === undefined) {
    return {
      text: 'Diese Person ist nicht mehr in dieser Reise. '
        + 'Der Verlauf bleibt lesbar, schreiben geht nicht mehr.',
      zurReise: false,
    }
  }
  if (partner.disabled) {
    return {
      text: 'Das Konto von {name} ist stillgelegt und kommt nirgends mehr herein.',
      zurReise: false,
    }
  }
  if (partner.cut_off) {
    return {
      text: '{name} ist aus dem Verteiler genommen und bekommt nichts Neues.',
      zurReise: true,
    }
  }
  return null
}
